import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { requestQuote } from "@/site/quote";
import { marketing, fmtMoney, fmtDate } from "@/site/api";
import demoWhitespaceFallback from "@/site/demoWhitespace";
import { track } from "@/site/analytics";

const LABEL = { live: "Live", impl: "Impl", ws: "Open", "n/a": "N/A" };

// One Open cell -> one calculator line. The server tells us the SKU and the
// quantity it priced the cell at (seats for per-user SKUs, 1 for flat).
const cellLine = (c) => ({ sku: c.sku, quantity: c.quantity || 1, module: c.module });
const openLines = (r) => r.cells.filter((c) => c.status === "ws" && c.sku).map(cellLine);

function quoteCell(e, r, c) {
  e.stopPropagation();  // don't toggle the row
  track("demo_grid_cell_quote", { account: r.name, module: c.module });
  requestQuote({ account: r.name, seats: r.seats, source: "cell", lines: [cellLine(c)] });
}
function quoteAccount(e, r) {
  e.preventDefault();
  track("demo_grid_quote_click", { account: r.name, modules: openLines(r).length });
  requestQuote({ account: r.name, seats: r.seats, source: "panel", lines: openLines(r) });
}

function Cell({ r, c, withModule }) {
  const body = <>{withModule && <small className="cell__mod">{c.module}</small>}{LABEL[c.status]}{c.status === "ws" && c.list_annual ? <small>{fmtMoney(c.list_annual)}</small> : null}</>;
  const cls = `cell cell--${c.status === "n/a" ? "na" : c.status} ${c.flip ? "flip" : ""}`;
  if (c.status === "ws" && c.sku) {
    return (
      <button type="button" className={`${cls} cell--quote`} onClick={(e) => quoteCell(e, r, c)}
              title={`Price ${c.module} for ${r.name}`} aria-label={`Price ${c.module} for ${r.name} in the calculator`}
              data-testid={`ws-quote-${r.id}-${c.sku}`}>
        {body}
      </button>
    );
  }
  return <span className={cls}>{body}</span>;
}

/**
 * Live, read-only whitespace grid: real component, fictional accounts, priced
 * from the marketing catalog by the backend. Click a row to see the account
 * summary; on first load one "Open" cell flips to "Impl" and the ARR ticks up
 * (the product in one gesture).
 */
export default function WhitespaceGrid({ demo = true }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [flipped, setFlipped] = useState(false);
  const [arrDelta, setArrDelta] = useState(0);

  useEffect(() => {
    marketing.demoWhitespace().then(setData).catch(() => setData(demoWhitespaceFallback));
  }, []);

  // Hero motion: flip Harbourline's Analytics cell from Open to Impl and tick ARR.
  useEffect(() => {
    if (!data || !demo || flipped) return;
    const t = setTimeout(() => {
      setFlipped(true);
      const row = data.rows[0];
      const cell = row.cells.find((c) => c.status === "ws");
      if (cell) setArrDelta(cell.list_annual || 0);
    }, 1600);
    return () => clearTimeout(t);
  }, [data, demo, flipped]);

  if (!data) return <div className="frame" style={{ padding: 24, color: "var(--muted)" }} data-testid="ws-loading">Loading the live grid…</div>;

  const rows = data.rows.map((r, i) => {
    if (i !== 0 || !flipped) return r;
    let done = false;
    return { ...r, arr: r.arr + arrDelta, headroom: Math.max(0, r.headroom - arrDelta),
      cells: r.cells.map((c) => (!done && c.status === "ws" ? (done = true, { ...c, status: "impl", flip: true }) : c)) };
  });
  const totals = { arr: data.totals.arr + (flipped ? arrDelta : 0), headroom: data.totals.headroom - (flipped ? arrDelta : 0) };
  const selRow = rows.find((r) => r.id === sel);

  return (
    <div className="frame ws" data-testid="whitespace-grid">
      <div className="frame__bar"><span>WHITESPACE · THE PRODUCT'S OWN GRID</span><span>Seeded accounts, real component — not a mock-up</span></div>
      <div className="ws__head">
        <div className="ws__title">Coverage matrix <span style={{ color: "var(--faint)", fontWeight: 400 }}>· {rows.length} accounts × {data.modules.length} modules · click any <b style={{ color: "var(--ink)" }}>Open</b> cell to price it</span></div>
        <div className="ws__totals">
          <span>Portfolio ARR <b className="num" data-testid="ws-total-arr">{fmtMoney(totals.arr)}</b></span>
          <span>Open at list <b className="num" data-testid="ws-total-headroom">{fmtMoney(totals.headroom)}</b></span>
        </div>
      </div>
      {/* Desktop / tablet: the table. Hidden under 640px (see .ws__scroll in App.css). */}
      <div className="ws__scroll" data-testid="ws-table-view">
        <table>
          <thead>
            <tr>
              <th>Account</th><th className="r">ARR</th>
              {data.modules.map((m) => <th key={m} style={{ textAlign: "center" }}>{m}</th>)}
              <th className="r">Headroom</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`row ${sel === r.id ? "sel" : ""}`} onClick={() => { setSel(sel === r.id ? null : r.id); track("demo_grid_row_click", { account: r.name }); }}
                  data-testid={`ws-row-${r.id}`} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setSel(sel === r.id ? null : r.id)} aria-selected={sel === r.id}>
                <td><div className="ws__name">{r.name}</div><div className="ws__sub">{r.region} · renews {fmtDate(r.renewal)}</div></td>
                <td className="r num">{fmtMoney(r.arr)}</td>
                {r.cells.map((c) => (
                  <td key={c.module} style={{ textAlign: "center" }}><Cell r={r} c={c} /></td>
                ))}
                <td className="r num">{r.headroom ? fmtMoney(r.headroom) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Phone: one card per account, module chips in a 2-column grid. Same data, same click. */}
      <div className="ws__cards" data-testid="ws-card-view">
        {rows.map((r) => (
          <div key={r.id} className={`ws__card ${sel === r.id ? "sel" : ""}`} role="button" tabIndex={0} aria-pressed={sel === r.id}
               onClick={() => { setSel(sel === r.id ? null : r.id); track("demo_grid_row_click", { account: r.name, view: "card" }); }}
               onKeyDown={(e) => e.key === "Enter" && setSel(sel === r.id ? null : r.id)} data-testid={`ws-card-${r.id}`}>
            <div className="ws__card-head">
              <div><div className="ws__name">{r.name}</div><div className="ws__sub">{r.region} · renews {fmtDate(r.renewal)}</div></div>
              <div className="ws__card-nums"><span>ARR <b className="num">{fmtMoney(r.arr)}</b></span><span>Open <b className="num">{r.headroom ? fmtMoney(r.headroom) : "—"}</b></span></div>
            </div>
            <div className="ws__chips">
              {r.cells.map((c) => <Cell key={c.module} r={r} c={c} withModule />)}
            </div>
          </div>
        ))}
      </div>
      {selRow && (
        <div className="ws__panel" data-testid="ws-panel">
          <div><div className="k">Account</div><div className="v" style={{ fontSize: 16 }}>{selRow.name} <small>· {selRow.seats} seats</small></div></div>
          <div><div className="k">Renewal</div><div className="v" style={{ fontSize: 16 }}>{fmtDate(selRow.renewal)}</div></div>
          <div><div className="k">Unsold at list</div><div className="v">{fmtMoney(selRow.headroom)} <small>fold into the renewal, not a later expansion</small></div></div>
          <Link to="/#calculator" className="btn btn--primary btn--sm" onClick={(e) => quoteAccount(e, selRow)} data-testid="ws-panel-quote">Price this deal → <small style={{ opacity: .8, fontWeight: 500 }}>{openLines(selRow).length} open module{openLines(selRow).length === 1 ? "" : "s"}</small></Link>
        </div>
      )}
    </div>
  );
}
